import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import {
  CRITERIA_LABELS,
  SCORING_FIELD_KEYS,
  type MarketSegmentWithScore,
  type ScoreField,
  type ScoringFieldKey,
} from '@ispot/shared'
import { FieldSourceBadge } from '../shared/FieldSourceBadge'
import { GroundingChipList } from '../shared/GroundingChipList'
import { RationaleHint } from '../shared/RationaleHint'

interface Props {
  segment: MarketSegmentWithScore
  onUpdateField: (key: string, value: number | null) => Promise<void>
  onResetField: (key: string) => Promise<void>
  onGenerateAI?: () => Promise<void>
}

export function SegmentScoringSection({
  segment,
  onUpdateField,
  onResetField,
  onGenerateAI,
}: Props) {
  const [generating, setGenerating] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)

  const handleGenerate = async () => {
    if (!onGenerateAI) return
    setGenerating(true)
    setAiError(null)
    try {
      await onGenerateAI()
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'AI scoring failed')
    } finally {
      setGenerating(false)
    }
  }

  const hasAnyAI = SCORING_FIELD_KEYS.some(
    (k) => segment.scoring[k]?.source === 'ai' || segment.scoring[k]?.source === 'edited',
  )
  return (
    <section
      className="space-y-5 rounded-[18px] px-5 py-5 lg:sticky lg:top-0"
      style={{ backgroundColor: 'var(--color-warm-50)' }}
    >
      <header className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-[28px] font-semibold tracking-[-0.02em] text-[var(--color-text)]">
            Scoring
          </h3>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            Weighted fit across strategy, market, serviceability, and profit.
          </p>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--color-text-muted)' }}>
            Weighted score
          </div>
          <div
            className="mt-1 font-display text-[40px] font-semibold leading-none tracking-[-0.03em]"
            style={{ color: 'var(--color-market)' }}
          >
            {segment.weightedScore.toFixed(1)}
          </div>
        </div>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!onGenerateAI || generating}
          className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            backgroundColor: 'rgba(255,255,255,0.7)',
            color: 'var(--color-text)',
          }}
          title={hasAnyAI ? 'Re-score with AI (preserves manual edits)' : 'Score with AI'}
        >
          <Sparkles className="h-3 w-3" style={{ color: 'var(--color-market)' }} />
          {generating ? 'Scoring…' : hasAnyAI ? 'Re-score with AI' : 'Score with AI'}
        </button>
      </header>
      {aiError ? (
        <p className="text-xs" style={{ color: '#b14132' }}>
          {aiError}
        </p>
      ) : null}

      <div className="space-y-1">
        {SCORING_FIELD_KEYS.map((key) => {
          const field = segment.scoring[key] ?? {
            value: null,
            source: 'manual' as const,
            aiValue: null,
            groundedEntities: [],
          }
          return (
            <ScoringRow
              key={key}
              criterion={key}
              field={field}
              onSubmit={(v) => onUpdateField(key, v)}
              onReset={() => onResetField(key)}
            />
          )
        })}
      </div>
    </section>
  )
}

interface RowProps {
  criterion: ScoringFieldKey
  field: ScoreField
  onSubmit: (value: number | null) => Promise<void>
  onReset: () => Promise<void>
}

function ScoringRow({ criterion, field, onSubmit, onReset }: RowProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(field.value === null ? '' : String(field.value))
  const label = CRITERIA_LABELS[criterion]
  const hasGrounding = (field.groundedEntities ?? []).length > 0

  const startEdit = () => {
    setDraft(field.value === null ? '' : String(field.value))
    setEditing(true)
  }
  const commit = async () => {
    setEditing(false)
    const parsed = draft.trim() === '' ? null : Number(draft)
    let next: number | null = null
    if (parsed !== null && Number.isFinite(parsed)) {
      next = Math.max(0, Math.min(10, parsed))
    }
    if (next === field.value) return
    await onSubmit(next)
  }
  const cancel = () => {
    setDraft(field.value === null ? '' : String(field.value))
    setEditing(false)
  }

  const pct = field.value === null ? 0 : (field.value / 10) * 100

  return (
    <div className="rounded-[14px] px-3 py-3 transition-colors hover:bg-white/75">
      <div className="grid items-center gap-3 md:grid-cols-[minmax(110px,130px)_1fr_auto]">
        <span
          className="text-[11px] font-medium uppercase tracking-wider"
          style={{ color: 'var(--color-text-muted)' }}
          title={label.description}
        >
          {label.label}
        </span>
        <div
          className="relative h-[6px] w-full overflow-hidden rounded-full"
          style={{ backgroundColor: 'var(--color-warm-200)' }}
          aria-hidden="true"
        >
          <div
            className="h-full rounded-full transition-[width] duration-[var(--dur-normal,180ms)]"
            style={{ width: `${pct}%`, backgroundColor: 'var(--color-market)' }}
          />
        </div>
        <div className="flex items-center justify-between gap-3 md:justify-end">
          {editing ? (
            <input
              autoFocus
              type="number"
              min={0}
              max={10}
              step={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void commit()
                if (e.key === 'Escape') cancel()
              }}
              className="w-12 rounded border px-2 py-0.5 text-right text-sm tabular-nums"
              style={{ borderColor: 'var(--color-border)' }}
            />
          ) : (
            <button
              type="button"
              onClick={startEdit}
              className="w-12 text-left font-display text-[18px] font-semibold tabular-nums transition-colors hover:text-[var(--color-market)]"
              style={{ color: 'var(--color-text)' }}
            >
              {field.value === null ? (
                <span style={{ color: 'var(--color-text-muted)' }} className="italic font-normal">
                  —
                </span>
              ) : (
                <>
                  {field.value}
                  <span
                    className="ml-0.5 text-[10px] font-normal"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    /10
                  </span>
                </>
              )}
            </button>
          )}
          <div className="flex items-center gap-1.5">
            <FieldSourceBadge
              source={field.source}
              aiValue={field.aiValue}
              currentValue={field.value}
              onReset={onReset}
              compact
            />
            <RationaleHint
              source={field.source}
              rationale={field.rationale}
              modelUsed={field.modelUsed}
              generatedAt={field.generatedAt}
              editedAt={field.editedAt}
              emptyHint={label.groundingHint}
            />
          </div>
        </div>
      </div>
      {hasGrounding ? (
        <div className="mt-2 text-xs md:pl-[122px]">
          <GroundingChipList entities={field.groundedEntities ?? []} />
        </div>
      ) : null}
    </div>
  )
}
